import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Column } from "primereact/column";
import { useAuth } from "../../context/AuthContext";
import FamilyFmeaApi from "../../services/FamilyFmeaApi";
import ProductionUnitApi from "../../services/ProductionUnitApi";
import AreaApi from "../../services/AreaApi";
import LineApi from "../../services/LineApi";
import SublineApi from "../../services/SublineApi";
import FamilyApi from "../../services/FamilyApi";
import { useToast } from "../../context/ToastContext";
import FmeaTypeEnum from "../../enum/FmeaTypeEnum";
import FmeaStatusEnum from "../../enum/FmeaStatusEnum";

// Design System
import PageHeader from "../../components/PageHeader/PageHeader";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import DataTable from "../../components/DataTable/DataTable";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import { Tag } from "../../components/Tag/Tag";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

const STORAGE_KEY = "ListFamilyFmea_filters";

interface FamilyFmea {
	id: string;
	code: string;
	title: string;
	fmeaType: number;
	description?: string;
	responsible?: string;
	status: number;
	productionUnit: string;
	productionUnitIds: string;
	area: string;
	areaIds: string;
	line: string;
	lineIds: string;
	subline: string;
	sublineIds: string;
	family: string;
	familyIds: string;
	familyId: string;
	sublineId: string;
}

const FmeaTypeLabels: Record<FmeaTypeEnum, string> = {
	[FmeaTypeEnum.AIAG_2008]: "AIAG 2008",
	[FmeaTypeEnum.AIAG_VDA_2019]: "AIAG/VDA 2019",
};

const normalizeFmeaTypeValue = (value: unknown): FmeaTypeEnum => {
	if (typeof value === "number") {
		return value as FmeaTypeEnum;
	}
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (!trimmed) {
			return FmeaTypeEnum.AIAG_2008;
		}
		const numeric = Number(trimmed);
		if (!Number.isNaN(numeric)) {
			return numeric as FmeaTypeEnum;
		}
		const mapped = (FmeaTypeEnum as unknown as Record<string, FmeaTypeEnum | undefined>)[trimmed];
		if (typeof mapped === "number") {
			return mapped;
		}
	}
	return FmeaTypeEnum.AIAG_2008;
};

/**
 * Os dois padrões de FMEA são processos distintos: o AIAG 2008 se trabalha pela lista de
 * falhas, o AIAG/VDA 2019 pelo fluxo de 7 etapas. A ação de trabalhar o FMEA precisa levar
 * ao processo do tipo do registro.
 */
const isAiagVda = (fmeaType: FmeaTypeEnum): boolean => fmeaType === FmeaTypeEnum.AIAG_VDA_2019;

const ListFamilyFmea: React.FC = () => {
	const { plantId } = useAuth();
	const { t: translateForm } = useTranslation("form");
	const { t: translateGeneral } = useTranslation("general");
	const { displayToast } = useToast();
	const navigate = useNavigate();

	const [familyFmeas, setFamilyFmeas] = useState<FamilyFmea[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rows, setRows] = useState(10);
	const [totalRecords, setTotalRecords] = useState(0);
	const [sortField, setSortField] = useState("code");
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

	// Filter states
	const [filterCode, setFilterCode] = useState("");
	const [filterTitle, setFilterTitle] = useState("");
	const [filterProductionUnitIds, setFilterProductionUnitIds] = useState<string[]>([]);
	const [filterAreaIds, setFilterAreaIds] = useState<string[]>([]);
	const [filterLineIds, setFilterLineIds] = useState<string[]>([]);
	const [filterSublineIds, setFilterSublineIds] = useState<string[]>([]);
	const [filterFamilyIds, setFilterFamilyIds] = useState<string[]>([]);
	const [filterFmeaType, setFilterFmeaType] = useState<number[]>([]);
	const [filterStatus, setFilterStatus] = useState<number[]>([]);

	const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
		const saved = localStorage.getItem(STORAGE_KEY);
		return saved ? JSON.parse(saved) : {};
	});

	const [familyOptions, setFamilyOptions] = useState<{ label: string; value: string }[]>([]);
	const [productionUnits, setProductionUnits] = useState<{ label: string; value: string }[]>([]);
	const [areas, setAreas] = useState<{ label: string; value: string }[]>([]);
	const [lines, setLines] = useState<{ label: string; value: string }[]>([]);
	const [sublines, setSublines] = useState<{ label: string; value: string }[]>([]);

	const statusOptions = [
		{ label: translateForm("active"), value: FmeaStatusEnum.Active },
		{ label: translateForm("inactive"), value: FmeaStatusEnum.Inactive },
	];

	const fmeaTypeOptions = useMemo(() => Object.values(FmeaTypeEnum)
		.filter((v) => typeof v === "number")
		.map((value) => ({ label: FmeaTypeLabels[value as FmeaTypeEnum], value: value as FmeaTypeEnum })), []);

	useEffect(() => {
		if (!plantId) return;
		const loadFilters = async (): Promise<void> => {
			try {
				const [respProd, respAreas, respLines, respSublines, respFamilies] = await Promise.all([
					ProductionUnitApi.GetProductUnitByPlant(plantId),
					AreaApi.GetAreaByPlantIdStatic(plantId),
					LineApi.GetLinesByPlant(plantId),
					SublineApi.GetSublineByPlant(plantId),
					FamilyApi.GetFamiliesByPlant(plantId),
				]);
				setProductionUnits(respProd.data.map((u: any) => ({ label: u.name, value: u.id })));
				setAreas(respAreas.data.map((u: any) => ({ label: u.name, value: u.id })));
				setLines(respLines.data.map((u: any) => ({ label: u.name, value: u.id })));
				setSublines(respSublines.data.map((u: any) => ({ label: u.name, value: u.id })));
				setFamilyOptions(respFamilies.data.map((u: any) => ({ label: u.name, value: u.id })));
			} catch (err: any) {
				displayToast("error", translateForm("error"), err.message || err, false, 3000);
			}
		};
		loadFilters();
	}, [plantId, displayToast, translateForm]);

	useEffect(() => {
		if (plantId) {
			// Initialize filters from saved state if any
			if (savedFilters.code) setFilterCode(savedFilters.code.value);
			if (savedFilters.title) setFilterTitle(savedFilters.title.value);
			if (savedFilters.productionUnitIds) setFilterProductionUnitIds(savedFilters.productionUnitIds.value);
			if (savedFilters.areaIds) setFilterAreaIds(savedFilters.areaIds.value);
			if (savedFilters.lineIds) setFilterLineIds(savedFilters.lineIds.value);
			if (savedFilters.sublineIds) setFilterSublineIds(savedFilters.sublineIds.value);
			if (savedFilters.familyIds) setFilterFamilyIds(savedFilters.familyIds.value);
			if (savedFilters.fmeaType) setFilterFmeaType(savedFilters.fmeaType.value);
			if (savedFilters.status) setFilterStatus(savedFilters.status.value);

			fetchFamilyFmeas(savedFilters, 0);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [plantId]);

	const fetchFamilyFmeas = async (
		filters: Record<string, any> = {},
		firstIndex = 0,
		field = sortField,
		direction: "asc" | "desc" = sortOrder,
		pageSize = rows,
	): Promise<void> => {
		setIsLoading(true);
		const plantIdsArray = plantId ? [plantId] : [];
		try {
			const prodIds = filters.productionUnitIds?.value || [];
			const areaIds = filters.areaIds?.value || [];
			const lineIds = filters.lineIds?.value || [];
			const sublineIds = filters.sublineIds?.value || [];
			const familyIds = filters.familyIds?.value || [];
			const title = filters.title?.value;
			const rawCode = filters.code?.value;
			const rawStatus: number[] = filters.status?.value || [];
			const statusParam = Array.isArray(rawStatus) && rawStatus.length > 0 ? rawStatus.map(String) : undefined;
			const code = rawCode && String(rawCode).trim() !== "" ? String(rawCode).trim() : undefined;
			const fmeaType = filters.fmeaType?.value;

			const response = await FamilyFmeaApi.GetByFilterAsync(
				plantIdsArray,
				Math.floor(firstIndex / pageSize) + 1,
				pageSize,
				field,
				direction,
				code,
				title,
				fmeaType,
				statusParam,
				familyIds,
				prodIds,
				areaIds,
				lineIds,
				sublineIds,
			);
			const data = response.data?.fmeas || [];
			setFamilyFmeas(
				data.map((ff: any) => ({
					...ff,
					familyId: ff.familyId,
					sublineId: ff.sublineId,
					fmeaType: normalizeFmeaTypeValue(ff.fmeaType ?? ff.fmea_type ?? ff.type),
					status: (ff.active === true || ff.isActive === true || ff.status === "Active") 
                               ? 1 
                               : (ff.status === "Inactive" || ff.status === 0 || ff.status === "0")
                                 ? 0
                                 : (ff.status !== undefined && ff.status !== null ? ff.status : 0),
				})),
			);
			setTotalRecords(typeof response.data?.total === "number" ? response.data.total : 0);
		} catch {
			setFamilyFmeas([]);
		} finally {
			setIsLoading(false);
		}
	};

	const handleApplyFilters = (): void => {
		const filters: Record<string, any> = {
			code: filterCode ? { value: filterCode } : undefined,
			title: filterTitle ? { value: filterTitle } : undefined,
			productionUnitIds: filterProductionUnitIds.length > 0 ? { value: filterProductionUnitIds } : undefined,
			areaIds: filterAreaIds.length > 0 ? { value: filterAreaIds } : undefined,
			lineIds: filterLineIds.length > 0 ? { value: filterLineIds } : undefined,
			sublineIds: filterSublineIds.length > 0 ? { value: filterSublineIds } : undefined,
			familyIds: filterFamilyIds.length > 0 ? { value: filterFamilyIds } : undefined,
			fmeaType: filterFmeaType.length > 0 ? { value: filterFmeaType } : undefined,
			status: filterStatus.length > 0 ? { value: filterStatus } : undefined,
		};

		// Clean undefined filters
		Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);

		setPage(0);
		setSavedFilters(filters);
		localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
		fetchFamilyFmeas(filters, 0);
	};

	const handleClearFilters = (): void => {
		setFilterCode("");
		setFilterTitle("");
		setFilterProductionUnitIds([]);
		setFilterAreaIds([]);
		setFilterLineIds([]);
		setFilterSublineIds([]);
		setFilterFamilyIds([]);
		setFilterFmeaType([]);
		setFilterStatus([]);

		setPage(0);
		setSavedFilters({});
		localStorage.removeItem(STORAGE_KEY);
		fetchFamilyFmeas({}, 0);
	};

	const handlePageChange = (e: PaginatorPageChangeEvent): void => {
		setPage(e.first);
		setRows(e.rows);
		fetchFamilyFmeas(savedFilters, e.first, sortField, sortOrder, e.rows);
	};

	const handleSortChange = (e: any): void => {
		const dir = e.sortOrder === 1 ? "asc" : "desc";
		setSortField(e.sortField);
		setSortOrder(dir);
		fetchFamilyFmeas(savedFilters, page, e.sortField, dir);
	};



	return (
		<div className="overflow-hidden">
			<PageHeader
				title={translateForm("familyFmeas")}
				description="Gerencie os FMEAs de famÃ­lia do sistema"
			>
				<Button
					label={translateForm("register") || "Novo"}
					icon="add"
					onClick={() => navigate("/family-fmeas/create")}
				/>
			</PageHeader>

			<div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
				<ConfirmDialog />
				
				<FilterPanel
					title="Filtros"
					onApply={handleApplyFilters}
					onClear={handleClearFilters}
				>
					<Input
						type="number"
						label={translateForm("code")}
						placeholder="Insira o cÃ³digo"
						value={filterCode}
						onChange={(val: any) => setFilterCode(val)}
					/>
					<Input
						type="text"
						label={translateForm("title")}
						placeholder="Pesquisar por tÃ­tulo"
						value={filterTitle}
						onChange={(val: any) => setFilterTitle(val)}
					/>
					<Input
						type="multiselect"
						label={translateForm("productionUnit")}
						placeholder="Selecione as unidades de produÃ§Ã£o"
						value={filterProductionUnitIds}
						options={productionUnits}
						onChange={(val: any) => setFilterProductionUnitIds(val)}
					/>
					<Input
						type="multiselect"
						label={translateForm("area")}
						placeholder="Selecione as Ã¡reas"
						value={filterAreaIds}
						options={areas}
						onChange={(val: any) => setFilterAreaIds(val)}
					/>
					<Input
						type="multiselect"
						label={translateForm("line")}
						placeholder="Selecione as linhas"
						value={filterLineIds}
						options={lines}
						onChange={(val: any) => setFilterLineIds(val)}
					/>
					<Input
						type="multiselect"
						label={translateForm("subline")}
						placeholder="Selecione as sublinhas"
						value={filterSublineIds}
						options={sublines}
						onChange={(val: any) => setFilterSublineIds(val)}
					/>
					<Input
						type="multiselect"
						label={translateForm("family")}
						placeholder="Selecione as famÃ­lias"
						value={filterFamilyIds}
						options={familyOptions}
						onChange={(val: any) => setFilterFamilyIds(val)}
					/>
					<Input
						type="multiselect"
						label={translateForm("fmeaType")}
						placeholder="Selecione os tipos de FMEA"
						value={filterFmeaType}
						options={fmeaTypeOptions}
						onChange={(val: any) => setFilterFmeaType(val)}
					/>
					<Input
						type="multiselect"
						label={translateForm("status")}
						placeholder="Selecione os status"
						value={filterStatus}
						options={statusOptions}
						onChange={(val: any) => setFilterStatus(val)}
					/>
				</FilterPanel>

				<DataTable
					value={familyFmeas}
					paginated
					first={page}
					rows={rows}
					totalRecords={totalRecords}
					onPageChange={handlePageChange}
					rowsPerPageOptions={[10, 20, 50]}
					loading={isLoading}
					sortField={sortField}
					sortOrder={sortOrder === "asc" ? 1 : -1}
					onSort={handleSortChange}
					emptyMessage={translateGeneral("noRecordsFound")}
				>
					<Column field="code" header={translateForm("code")} sortable body={(row: FamilyFmea) => row.code} style={{ minWidth: "8rem" }} />
					<Column field="title" header={translateForm("title")} sortable style={{ minWidth: "12rem" }} />
					<Column field="productionUnit" header={translateForm("productionUnit")} sortable style={{ minWidth: "10rem" }} body={(row: any) => row.productionUnit || "N/A"} />
					<Column field="area" header={translateForm("area")} sortable style={{ minWidth: "10rem" }} body={(row: any) => row.area || "N/A"} />
					<Column field="line" header={translateForm("line")} sortable style={{ minWidth: "10rem" }} body={(row: any) => row.line || "N/A"} />
					<Column field="subline" header={translateForm("subline")} sortable style={{ minWidth: "10rem" }} body={(row: any) => row.subline || "N/A"} />
					<Column field="family" header={translateForm("family")} sortable style={{ minWidth: "10rem" }} body={(row: any) => row.family || "N/A"} />
					<Column 
						field="fmeaType" 
						header={translateForm("fmeaType")} 
						sortable
						style={{ minWidth: "10rem" }} 
						body={(row: any) => FmeaTypeLabels[row.fmeaType as FmeaTypeEnum] || ""} 
					/>
					<Column 
						field="status" 
						header={translateForm("status")} 
						sortable
						style={{ minWidth: "6rem" }} 
						body={(row: FamilyFmea) => (
							<div style={{ display: "flex", justifyContent: "center" }}>
								<Tag 
									label={row.status === 1 ? translateForm("active") : translateForm("inactive")} 
									severity={row.status === 1 ? "successInverse" : "errorInverse"} 
								/>
							</div>
						)} 
					/>
					<Column 
						field="actions" 
						header={translateForm("actions")} 
						body={(row: FamilyFmea) => (
							<div style={{ display: "flex", gap: "0.25rem" }}>
								<IconButton 
									icon="visibility" 
									variant="text" 
									severity="secondary" 
									onClick={() => navigate(`/family-fmeas/${row.id}`)} 
									aria-label={translateGeneral("seeDetails")}
								/>
								<IconButton 
									icon="edit" 
									variant="text" 
									severity="secondary" 
									onClick={() => navigate(`/family-fmeas/${row.id}/edit`)} 
									aria-label={translateGeneral("edit")}
								/>
								{/*
									O fluxo de etapas é uma rota só (/fmeas/:fmeaId/steps/N) e atende os dois
									tipos: o backend indexa a estrutura AIAG/VDA por FmeaAiagVda.FmeaId, que
									guarda o id do próprio FMEA de família. Mesmo destino usado pela criação.
								*/}
								<IconButton
									icon="warning"
									variant="text"
									severity="secondary"
									onClick={() => navigate(
										isAiagVda(row.fmeaType)
											? `/fmeas/${row.id}/steps/1`
											: `/family-fmeas/${row.id}/failures?type=family`
									)}
									aria-label={isAiagVda(row.fmeaType)
										? translateGeneral("openFmeaSteps")
										: translateGeneral("manageFailures")}
								/>
							</div>
						)} 
						style={{ width: "10rem" }}
					/>
				</DataTable>
			</div>
		</div>
	);
};

export default ListFamilyFmea;
